import { Box, IconButton, styled, useTheme, Button } from '@mui/material';
import { FC, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { ReactComponent as ChevronRightIcon } from '../../assets/icons/chevron-right.svg';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { Typography } from '../Typography/Typography';

export interface MonthYear {
  month: number;
  year: number;
}

interface MonthlyRangeSelectorProps {
  startDate?: MonthYear;
  endDate?: MonthYear;
  open: boolean;
  handleOnClose: () => void;
  onRangeChange: (startDate: MonthYear | null, endDate: MonthYear | null) => void;
  shouldDisableFuture?: boolean;
}

interface RangeSelectorDropdownProps {
  startDate?: MonthYear | null;
  endDate?: MonthYear | null;
  onRangeChange: (startDate: MonthYear | null, endDate: MonthYear | null) => void;
  onClose: () => void;
  shouldDisableFuture?: boolean;
}

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

const TriggerWrapper = styled(Box)`
  position: relative;
  display: inline-block;
`;

const DropdownWrapper = styled(Box)`
  position: absolute;
  top: 120px;
  left: -5px;
  z-index: 1000;
  background-color: ${({ theme }) => theme.colors.surfaceBackground.bg1};
  border-radius: 8px;
  width: 480px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.5);
`;

const Header = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 12px 8px;
`;

const RangeContainer = styled(Box)`
  display: flex;
  gap: 16px;
  padding: 0 16px;
`;

const ContainerDivider = styled(Box)`
  border-right: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const MonthsGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 8px 0;
`;

const ButtonContainer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 16px 16px 16px;
`;

const RangeSelectorDropdown: FC<RangeSelectorDropdownProps> = ({
  startDate,
  endDate,
  onRangeChange,
  onClose,
  shouldDisableFuture = true,
}) => {
  const { colors } = useTheme();
  const currentDate = useMemo(() => {
    return new Date();
  }, []);
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const [startYear, setStartYear] = useState(startDate?.year || currentYear);
  const [endYear, setEndYear] = useState(endDate?.year || currentYear);
  const [tempStartDate, setTempStartDate] = useState<MonthYear | null>(startDate || null);
  const [tempEndDate, setTempEndDate] = useState<MonthYear | null>(endDate || null);

  const isMonthDisabled = useCallback(
    (monthIndex: number, year: number) => {
      if (!shouldDisableFuture) return false;
      return year > currentYear || (year === currentYear && monthIndex >= currentMonth);
    },
    [shouldDisableFuture, currentYear, currentMonth]
  );

  const isMonthInRange = useCallback(
    (monthIndex: number, year: number) => {
      if (!tempStartDate || !tempEndDate) return false;

      const start = tempStartDate;
      const end = tempEndDate;

      const dateValue = year * 12 + monthIndex;
      const startValue = start.year * 12 + start.month;
      const endValue = end.year * 12 + end.month;

      return dateValue >= startValue && dateValue <= endValue;
    },
    [tempStartDate, tempEndDate]
  );

  const isMonthSelected = useCallback(
    (monthIndex: number, year: number) => {
      return (
        (tempStartDate?.month === monthIndex && tempStartDate?.year === year) ||
        (tempEndDate?.month === monthIndex && tempEndDate?.year === year)
      );
    },
    [tempStartDate, tempEndDate]
  );

  const handleStartMonthClick = useCallback(
    (monthIndex: number, year: number) => {
      if (isMonthDisabled(monthIndex, year)) return;

      const selectedDate = { month: monthIndex, year };
      setTempStartDate(selectedDate);

      // Clear end date if it's before the new start date
      if (tempEndDate) {
        const selectedValue = year * 12 + monthIndex;
        const endValue = tempEndDate.year * 12 + tempEndDate.month;
        if (selectedValue > endValue) {
          setTempEndDate(null);
        }
      }
    },
    [tempEndDate, isMonthDisabled]
  );

  const handleEndMonthClick = useCallback(
    (monthIndex: number, year: number) => {
      if (isMonthDisabled(monthIndex, year)) return;

      const selectedDate = { month: monthIndex, year };

      // Only set end date if it's after or equal to start date
      if (tempStartDate) {
        const selectedValue = year * 12 + monthIndex;
        const startValue = tempStartDate.year * 12 + tempStartDate.month;

        if (selectedValue >= startValue) {
          setTempEndDate(selectedDate);
        }
      } else {
        setTempEndDate(selectedDate);
      }
    },
    [tempStartDate, isMonthDisabled]
  );

  const handleApply = useCallback(() => {
    onRangeChange(tempStartDate, tempEndDate);
    onClose();
  }, [tempStartDate, tempEndDate, onRangeChange, onClose]);

  const handleClear = useCallback(() => {
    setTempStartDate(null);
    setTempEndDate(null);
    onRangeChange(null, null);
    onClose();
  }, [onRangeChange, onClose]);

  const renderMonthGrid = (
    year: number,
    onYearChange: (year: number) => void,
    isStartGrid: boolean
  ) => (
    <Box flex={1}>
      <Header>
        <IconButton onClick={() => onYearChange(year - 1)} size='small'>
          <ChevronRightIcon style={{ rotate: '180deg' }} />
        </IconButton>
        <Typography variant='h4' color={colors.text.main}>
          {year}
        </Typography>
        <IconButton
          onClick={() => onYearChange(year + 1)}
          size='small'
          disabled={shouldDisableFuture && year >= currentYear}
          style={{
            opacity: shouldDisableFuture && year >= currentYear ? 0.5 : 1,
            cursor: shouldDisableFuture && year >= currentYear ? 'not-allowed' : 'pointer',
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Header>
      <MonthsGrid>
        {MONTHS.map((month, index) => {
          const disabled = isMonthDisabled(index, year);
          const selected = isMonthSelected(index, year);
          const inRange = isMonthInRange(index, year);

          return (
            <Button
              variant='text'
              key={month}
              onClick={() =>
                isStartGrid ? handleStartMonthClick(index, year) : handleEndMonthClick(index, year)
              }
              disabled={disabled}
              style={{
                backgroundColor: selected
                  ? colors.surfaceAccent.default
                  : inRange
                  ? colors.surfaceBackground.highlighted
                  : 'transparent',
                borderRadius: '20px',
                transition: 'all 0.2s ease',
                opacity: disabled ? 0.5 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
                minHeight: '32px',
                padding: '4px 8px',
              }}
            >
              <Typography
                variant='body'
                color={
                  selected
                    ? colors.text.main
                    : disabled
                    ? colors.textAccent.disabled
                    : colors.text.main
                }
              >
                {month}
              </Typography>
            </Button>
          );
        })}
      </MonthsGrid>
    </Box>
  );

  return (
    <DropdownWrapper>
      <RangeContainer>
        {renderMonthGrid(startYear, setStartYear, true)}
        <ContainerDivider />
        {renderMonthGrid(endYear, setEndYear, false)}
      </RangeContainer>

      <ButtonContainer>
        <Button variant='text' onClick={handleClear}>
          Clear
        </Button>
        <Button variant='contained' onClick={handleApply} disabled={!tempStartDate || !tempEndDate}>
          Apply
        </Button>
      </ButtonContainer>
    </DropdownWrapper>
  );
};

export const MonthlyRangeSelector: FC<MonthlyRangeSelectorProps> = ({
  startDate,
  endDate,
  open,
  handleOnClose,
  onRangeChange,
  shouldDisableFuture = true,
}) => {
  const [isOpen, setIsOpen] = useState(open);
  const boxRef = useRef(null);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useOutsideClick(boxRef, () => {
    handleOnClose();
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const handleClose = useCallback(() => {
    handleOnClose();
    setIsOpen(false);
  }, [handleOnClose]);

  return (
    <TriggerWrapper ref={boxRef}>
      {isOpen && (
        <RangeSelectorDropdown
          startDate={startDate}
          endDate={endDate}
          onRangeChange={onRangeChange}
          onClose={handleClose}
          shouldDisableFuture={shouldDisableFuture}
        />
      )}
    </TriggerWrapper>
  );
};
